import { AbstractBuildstep } from "@project/emerge";
import groupBy from "lodash/groupBy";
import { Covers } from "../enum/Covers";
import { EdgeIndex } from "../enum/EdgeIndex";
import { EdgeType } from "../enum/EdgeType";
import { LTCaseUserOption } from "../enum/LTCaseUserOption";
import { LTProducts } from "../enum/LTProducts";
import { LTPartType } from "../enum/PartType";
import { RackCoverPaddingStyle as CoverPaddingStyle } from "../enum/RackCoverPaddingStyle";
import { RectType } from "../enum/RectType";
import type { CoverPaddingSettings } from "../interfaces/CoverPaddingSettings";
import type { FlightCaseBlueprint } from "../interfaces/FlightCaseBlueprint";
import type { FlightCaseSettings } from "../interfaces/FlightCaseSettings";
import type { PaddingType } from "../interfaces/Padding";
import type { PadDefinition } from "../interfaces/PadProperties";
import type { ProfileType } from "../interfaces/ProfileType";
import { DefaultGroupName, type Rect } from "../structure/Rect";
import { RectSizeHelper } from "../structure/RectSizeHelper";
import { OptionDBInstance } from "../user_options/options_db";
import type { InteriorPadDependencyData } from "./interior-helpers/InteriorTypes";
import { applyPadVolumeCenterWorldOffset } from "./interior-helpers/padPositionOffset";

/**
 * CoverPaddingBuildStep handles cover padding (Deckelpolsterung) for ALL interior types.
 * This is separate from the interior build steps to ensure consistent padding regardless
 * of whether the case uses CASE, RACK, or RACK19 interior type.
 */
export class CoverPaddingBuildStep extends AbstractBuildstep<
  FlightCaseBlueprint,
  FlightCaseSettings,
  PadDefinition,
  InteriorPadDependencyData
> {
  constructor() {
    super("CoverPaddingBuildStep");
  }

  private resolvePaddingRef(raw: unknown): PaddingType | undefined {
    if (!raw || typeof raw !== "object" || Array.isArray(raw)) return undefined;
    if ((raw as PaddingType).strength !== undefined) return raw as PaddingType;
    const name =
      typeof (raw as { name?: string }).name === "string"
        ? ((raw as { name?: string }).name ?? "").trim()
        : "";
    if (!name) return undefined;
    const found = OptionDBInstance.get(LTCaseUserOption.PaddingType, { name });
    return found as PaddingType | undefined;
  }

  private resolveCoverSettingsRefs(
    s: CoverPaddingSettings,
  ): CoverPaddingSettings {
    const area = this.resolvePaddingRef(s.areaPaddingType);
    const border = this.resolvePaddingRef(s.borderPaddingType);
    if (!area && !border) return s;
    return {
      ...s,
      ...(area && { areaPaddingType: area }),
      ...(border && { borderPaddingType: border }),
    };
  }

  private shouldProcessCoverRect(
    rect: Rect,
    coverSettings: CoverPaddingSettings,
  ): boolean {
    const isCoverPlate = rect.type === RectType.COVERPLATE;
    if (!isCoverPlate && coverSettings.borderPaddingType.strength === 0)
      return false;
    if (isCoverPlate && coverSettings.areaPaddingType.strength === 0)
      return false;
    return true;
  }

  private shrinkCoverEdges(
    rect: Rect,
    padSize: RectSizeHelper,
    settings: FlightCaseSettings,
    upperPaddingAlignment: boolean,
    coverSettings: CoverPaddingSettings,
  ): void {
    const { profiles } = settings.exteriorOptions;
    for (const edgeIndex of [
      EdgeIndex.RIGHT,
      EdgeIndex.TOP,
      EdgeIndex.LEFT,
      EdgeIndex.BOTTOM,
    ]) {
      const edge = rect.edges[edgeIndex];
      if (!edge) continue;
      if (edge.type === EdgeType.FROMCUT) {
        this.handleFromCutEdge(
          edge,
          edgeIndex,
          padSize,
          profiles,
          settings,
          upperPaddingAlignment,
        );
      } else {
        this.handleNeighbourEdge(rect, edgeIndex, padSize, coverSettings);
      }
    }
  }

  private handleFromCutEdge(
    edge: { inverted?: boolean },
    edgeIndex: EdgeIndex,
    padSize: RectSizeHelper,
    profiles: ProfileType,
    settings: FlightCaseSettings,
    upperPaddingAlignment: boolean,
  ): void {
    if (upperPaddingAlignment) {
      const delta = profiles.extra / 2 - profiles.strength;
      if (
        (edge.inverted ?? false) !==
        (profiles as { inverted?: boolean }).inverted
      ) {
        padSize.shrink(edgeIndex, delta);
      } else {
        padSize.expand(edgeIndex, delta);
      }
      if (
        [LTProducts.Haube, LTProducts.KeyboardCase].indexOf(settings.type) ===
        -1
      ) {
        padSize.shrink(edgeIndex, 1);
      }
      return;
    }
    padSize.shrink(edgeIndex, profiles.height + profiles.extra / 2);
  }

  private handleNeighbourEdge(
    rect: Rect,
    edgeIndex: EdgeIndex,
    padSize: RectSizeHelper,
    coverSettings: CoverPaddingSettings,
  ): void {
    const neighbour = rect.getNeighbour(edgeIndex);
    const isCoverPlate = rect.type === RectType.COVERPLATE;
    if (
      neighbour &&
      neighbour.type === RectType.COVERSIDE &&
      (isCoverPlate || rect.side < neighbour.side)
    ) {
      padSize.shrink(edgeIndex, coverSettings.borderPaddingType.strength);
    }
  }

  private addCoverPlatePads(
    rect: Rect,
    padSize: RectSizeHelper,
    coverSettings: CoverPaddingSettings,
    dependencies: Map<string, InteriorPadDependencyData>,
    counter: { value: number },
  ): void {
    // Skip pads with 0mm strength
    if (coverSettings.areaPaddingType.strength <= 0) {
      return;
    }

    const padId = `cover_pad_${counter.value++}`;
    // Match original: use padSize.innerPos3d directly (not cloned) for basePad
    const basePad: InteriorPadDependencyData = {
      partId: padId,
      partKind: "InteriorPad",
      type: LTPartType.Pad,
      group: rect.group,
      buildstepName: this.buildstepName,
      position: padSize.innerPos3d,
      side: rect.side,
      size: [padSize.size.x, padSize.size.y],
      strength: coverSettings.areaPaddingType.strength,
      style: coverSettings.areaPaddingType.style,
      orientation: 0,
      partSelection: coverSettings.areaPaddingType,
    };

    if (coverSettings.areaPaddingStyle === CoverPaddingStyle.CENTER) {
      // Skip if width is 0 or invalid
      if (!coverSettings.width || coverSettings.width <= 0) {
        return;
      }
      basePad.size[0] = coverSettings.width;
      dependencies.set(padId, basePad);
      return;
    }

    if (coverSettings.areaPaddingStyle === CoverPaddingStyle.BORDER) {
      // Skip if width is 0 or invalid
      if (!coverSettings.width || coverSettings.width <= 0) {
        return;
      }
      const delta = basePad.size[0] - coverSettings.width;
      const padLeft: InteriorPadDependencyData = {
        ...basePad,
        partId: `cover_pad_${counter.value++}`,
        position: basePad.position.clone(),
      };
      const padRight: InteriorPadDependencyData = {
        ...basePad,
        partId: `cover_pad_${counter.value++}`,
        position: basePad.position.clone(),
      };
      padLeft.position.x -= delta / 2;
      padRight.position.x += delta / 2;
      padLeft.size[0] = coverSettings.width;
      padRight.size[0] = coverSettings.width;

      dependencies.set(padLeft.partId, padLeft);
      dependencies.set(padRight.partId, padRight);
      return;
    }

    // FULL width
    dependencies.set(padId, basePad);
  }

  private addBorderPad(
    rect: Rect,
    padSize: RectSizeHelper,
    coverSettings: CoverPaddingSettings,
    dependencies: Map<string, InteriorPadDependencyData>,
    counter: { value: number },
  ): void {
    const isCoverPlate = rect.type === RectType.COVERPLATE;

    const paddingType = isCoverPlate
      ? coverSettings.areaPaddingType
      : coverSettings.borderPaddingType;

    // Skip pads with 0mm strength
    if (paddingType.strength <= 0) {
      return;
    }

    const padId = `cover_pad_${counter.value++}`;

    const dep: InteriorPadDependencyData = {
      partId: padId,
      partKind: "InteriorPad",
      type: LTPartType.Pad,
      group: rect.group,
      buildstepName: this.buildstepName,
      position: padSize.innerPos3d,
      side: rect.side,
      size: [padSize.size.x, padSize.size.y],
      strength: paddingType.strength,
      style: paddingType.style,
      orientation: 0,
      partSelection: paddingType,
    };

    dependencies.set(padId, dep);
  }

  private calculateCoverPaddingDeps(
    rects: Rect[],
    settings: FlightCaseSettings,
    coverSettings: CoverPaddingSettings,
    upperPaddingAlignment: boolean,
    dependencies: Map<string, InteriorPadDependencyData>,
    counter: { value: number },
  ): void {
    for (const rect of rects) {
      if (!this.shouldProcessCoverRect(rect, coverSettings)) {
        continue;
      }

      const padSize = new RectSizeHelper(rect);
      this.shrinkCoverEdges(
        rect,
        padSize,
        settings,
        upperPaddingAlignment,
        coverSettings,
      );

      const isCoverPlate = rect.type === RectType.COVERPLATE;

      if (
        isCoverPlate &&
        coverSettings.areaPaddingStyle !== CoverPaddingStyle.FULL
      ) {
        this.addCoverPlatePads(
          rect,
          padSize,
          coverSettings,
          dependencies,
          counter,
        );
      } else {
        this.addBorderPad(rect, padSize, coverSettings, dependencies, counter);
      }
    }
  }

  private resolveCoverPaddingForGroup(
    settings: FlightCaseSettings,
    groupName: string,
  ): CoverPaddingSettings | undefined {
    const candidates: Array<CoverPaddingSettings | undefined> = [];

    candidates.push(settings.coverPaddings[groupName]);

    const alternativeKeys = this.getAlternativeCoverKeysForGroup(groupName);
    for (const alt of alternativeKeys) {
      candidates.push(
        settings.coverPaddings[alt as keyof typeof settings.coverPaddings],
      );
    }

    const resolved = (c?: CoverPaddingSettings) =>
      c ? this.resolveCoverSettingsRefs(c) : undefined;
    const hasStrength = (c?: CoverPaddingSettings) =>
      !!c &&
      ((c.areaPaddingType?.strength ?? 0) > 0 ||
        (c.borderPaddingType?.strength ?? 0) > 0);

    const preferred = candidates.find(hasStrength);
    if (preferred) return resolved(preferred);
    const fallback = candidates.find((c) => !!c);
    return fallback ? resolved(fallback) : undefined;
  }

  private getAlternativeCoverKeysForGroup(groupName: string): string[] {
    const map: Record<string, string> = {
      COVER: Covers.COVER,
      BASE: Covers.BASE,
      FRONT_COVER: Covers.FRONT_COVER,
      BACK_COVER: Covers.BACK_COVER,
      TOP_COVER: Covers.TOP_COVER,
    } as const as Record<string, string>;

    const alts: string[] = [];

    for (const [enumKey, localized] of Object.entries(map)) {
      if (localized === groupName) alts.push(enumKey);
    }

    if (map[groupName]) alts.push(map[groupName]);

    return alts;
  }

  private addCoverPaddingDeps(
    rectsByPartGroup: Record<string, Rect[]>,
    settings: FlightCaseSettings,
    dependencies: Map<string, InteriorPadDependencyData>,
    counter: { value: number },
  ): void {
    for (const groupName in rectsByPartGroup) {
      if (groupName !== DefaultGroupName) {
        const rects = rectsByPartGroup[groupName];
        const coverPadding = this.resolveCoverPaddingForGroup(
          settings,
          groupName,
        );
        if (rects && coverPadding) {
          const isHood =
            settings.type === LTProducts.Haube ||
            settings.type === LTProducts.KeyboardCase;
          this.calculateCoverPaddingDeps(
            rects,
            settings,
            coverPadding,
            isHood ? settings.interiorOptions.upperPaddingAlignment : true,
            dependencies,
            counter,
          );
        }
      }
    }
  }

  public selectDependencies(
    blueprint: FlightCaseBlueprint,
    settings: FlightCaseSettings,
  ): Map<string, InteriorPadDependencyData> {
    const dependencies = new Map<string, InteriorPadDependencyData>();
    const counter = { value: 0 };
    const rectsByPartGroup = groupBy(blueprint.rects, "group");

    this.addCoverPaddingDeps(rectsByPartGroup, settings, dependencies, counter);

    return dependencies;
  }

  public createPart(
    partId: string,
    dep: InteriorPadDependencyData,
  ): PadDefinition {
    const rotation = this.rotateOntoSide(dep.side, dep.orientation);
    return {
      id: partId,
      type: dep.type,
      group: dep.group,
      buildstepName: this.buildstepName,
      position: applyPadVolumeCenterWorldOffset(
        dep.position,
        dep.strength,
        rotation,
      ),
      side: dep.side,
      size: dep.size,
      strength: dep.strength,
      style: dep.style,
      rotation,
      orientation: dep.orientation,
      partSelection: dep.partSelection,
      cutouts: [],
    };
  }
}
